/** Client portal: the Performance Dashboard (closed months only), published reports, action items and the next meeting. */
import { useQuery } from "@tanstack/react-query";
import { lazy, Suspense } from "react";
import { CalendarDays, CheckCircle2, Circle, FileText } from "lucide-react";
import { Empty, Loading, PageHeader, Section } from "@/components/page";
import { formatDate } from "@/lib/format";
import { TableScroll } from "@/components/table-scroll";

type Card = { clientId: string; periods: Array<{ period: string; asOf: string }>; scorecard: Array<{ key: string; label: string; formula: string; values: Array<{ period: string; display: string; light: string }> }>; dsoBasis: string };
type Advisory = { clientId: string; reports: Array<{ id: string; period: string; documentId: string | null }>; actions: Array<{ id: string; title: string; owner: string; dueOn: string | null }>; nextMeeting: { title: string; meetingOn: string } | null };

const PerformanceDashboard = lazy(() => import("@/components/performance-dashboard"));
const SharedPlans = lazy(() => import("@/components/plan-workspace").then((m) => ({ default: m.SharedPlans })));

const monthLabel = (p: string) => new Date(`${p}-15T12:00:00`).toLocaleDateString("en-US", { month: "short", year: "numeric" });
const LIGHT: Record<string, string> = { green: "text-emerald-700 dark:text-emerald-300", amber: "text-amber-700 dark:text-amber-300", red: "text-destructive", none: "" };

export function PortalCfoPage({ clientName }: { clientName: (id: string) => string | undefined }) {
  const cards = useQuery<Card[]>({ queryKey: ["/api/portal/cfo"] });
  const adv = useQuery<Advisory[]>({ queryKey: ["/api/portal/cfo/advisory"] });
  if (cards.isLoading || adv.isLoading) return <Loading tall />;
  const ids = [...new Set([...(cards.data ?? []).map((c) => c.clientId), ...(adv.data ?? []).map((a) => a.clientId)])];
  return (
    <>
      <PageHeader title="Performance Dashboard" description={cards.data?.length ? "Your business's results from closed months, compared with your budget and last year, with CFO reports and agreed action items." : "Your CFO reports, agreed action items and next meeting."} />
      {!ids.length && <Empty>You are not currently engaged for CFO & Advisory services.</Empty>}
      <div className="space-y-8">
        {ids.map((id) => {
          const c = cards.data?.find((x) => x.clientId === id);
          const a = adv.data?.find((x) => x.clientId === id);
          const last = c?.periods.slice(-6) ?? [];
          return (
            <div key={id} className="space-y-4">
              {ids.length > 1 && <h2 className="text-lg font-semibold">{clientName(id)}</h2>}
              {a?.nextMeeting && (
                <p className="flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm">
                  <CalendarDays className="h-4 w-4 text-primary" /> Next meeting: {a.nextMeeting.title}, {formatDate(a.nextMeeting.meetingOn)}
                </p>
              )}
              {c && (
                <Suspense fallback={<Loading tall />}>
                  <PerformanceDashboard endpoint="/api/portal/cfo/dashboard" audience="client" clientId={id} />
                </Suspense>
              )}
              {c && (
                <Suspense fallback={null}>
                  <SharedPlans clientId={id} />
                </Suspense>
              )}
              {c && (
              <Section title="All indicators" description="Each indicator for the last six closed months.">
                {!last.length ? (
                  <Empty>Indicators appear here after each month-end close.</Empty>
                ) : (
                  <TableScroll label="All indicators table">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs text-muted-foreground">
                          <th className="py-1.5 pr-3">KPI</th>
                          {last.map((p) => (
                            <th key={p.period} className="px-2 py-1.5 text-right">
                              {monthLabel(p.period)}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {c.scorecard.map((k) => (
                          <tr key={k.key} className="border-t border-border">
                            <td className="py-1.5 pr-3" title={k.formula}>
                              {k.label}
                            </td>
                            {last.map((p) => {
                              const v = k.values.find((x) => x.period === p.period);
                              return (
                                <td key={p.period} className={`px-2 py-1.5 text-right tabular-nums ${LIGHT[v?.light ?? "none"]}`}>
                                  {v?.display ?? "—"}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <p className="mt-2 text-xs text-muted-foreground">Calculated from your closed books in QuickBooks. Colors show each indicator against the target agreed with your firm. {c.dsoBasis}</p>
                  </TableScroll>
                )}
              </Section>
              )}
              <div className="grid gap-4 lg:grid-cols-2">
                <Section title="Action items">
                  {!a?.actions.length ? (
                    <Empty>No open action items.</Empty>
                  ) : (
                    <ul className="space-y-2 text-sm">
                      {a.actions.map((x) => (
                        <li key={x.id} className="flex items-start gap-2">
                          {x.owner === "client" ? <Circle className="mt-0.5 h-4 w-4 text-primary" /> : <CheckCircle2 className="mt-0.5 h-4 w-4 text-muted-foreground" />}
                          <span className="flex-1">
                            {x.title}
                            <span className="block text-xs text-muted-foreground">
                              {x.owner === "client" ? "Owner: You" : "Owner: Your firm"}
                              {x.dueOn && ` · Due ${formatDate(x.dueOn)}`}
                            </span>
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </Section>
                <Section title="CFO reports">
                  {!a?.reports.length ? (
                    <Empty>Monthly CFO reports will appear here once published.</Empty>
                  ) : (
                    <ul className="divide-y divide-border">
                      {a.reports.map((r) => (
                        <li key={r.id} className="flex items-center gap-3 py-2">
                          <FileText className="h-4 w-4 text-muted-foreground" />
                          <a className="flex-1 text-sm hover:underline" href={`/api/portal/cfo/reports/${r.id}/download`}>
                            CFO report — {monthLabel(r.period)}
                          </a>
                        </li>
                      ))}
                    </ul>
                  )}
                </Section>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}
